import { useFetchSearchOrders } from '@/hooksApi/useOrder';
import { OrderListResponse } from '@/types';
import { formatDateByDayjs, getOrderPickupAddress } from '@/utils';
import { ISearch } from './useSearchStore';
import { MerchantTitleOrderStatusEnum, OrderStatusEnum } from '@/config';
import { OrderFilters } from '@/stores/useFilterOrderStore';
import { getOrderParams } from '@/utils';
import { orderReasonText } from '@/utils/orderReason';

const useOrderListData = (
  current: number,
  pageSize: number,
  searchValue?: ISearch,
  filters?: OrderFilters,
  statusGroup?: string,
  statusSlugFilter?: string,
) => {
  const filterParams = filters ? getOrderParams(filters) : undefined;

  // Build extra Meilisearch filters for sub-status when sub-tab is active
  const statusFilters = statusSlugFilter
    ? [{ key: 'status.slug', value: statusSlugFilter }]
    : undefined;

  // data with filter — scope to active tab via statusGroup + optional sub-status
  const {
    data: dataOrderList,
    isLoading: loadingOrderList,
    isFetching: fetchingOrderList,
  } = useFetchSearchOrders(current, pageSize, searchValue, filterParams, statusGroup, statusFilters);

  // transform data
  const mapOrderData = (data: OrderListResponse | undefined) => {
    if (!data?.object) return [];
    return (
      data?.object?.items?.map((item) => ({
        key: item.uid,
        orderDate: formatDateByDayjs(item.created_at, 'D MMMM YYYY'),
        orderDateRaw: item.created_at,
        // FE-2: Pull the timestamp when the order was first moved to any RTO
        // status (rto-requested / rto-request-pending). Lets the RTO /
        // Returned tab show "Requested: <date>" alongside the created date,
        // so merchants can distinguish when the return was initiated from
        // when the order was originally placed.
        rtoRequestedAt: (() => {
          // Search endpoint exposes a flat `rto_requested_at` (backend hydrates
          // it from pivot). List endpoint returns the full `statuses` history.
          // Accept either shape so the RTO timestamp renders in both views.
          const flat = (item as any)?.rto_requested_at;
          if (flat) return formatDateByDayjs(flat, 'D MMMM YYYY');
          const rtoStatus = (item as any)?.statuses?.find(
            (s: any) => s?.slug === 'rto-requested' || s?.slug === 'rto-request-pending',
          );
          const ts = rtoStatus?.pivot?.created_at;
          return ts ? formatDateByDayjs(ts, 'D MMMM YYYY') : null;
        })(),
        orderId: item?.platform_code,
        customerName: item?.full_name,
        customerAddress: item?.address_text,
        orderValue: item?.products_value ?? item?.products?.reduce((sum: number, p: any) => sum + (p?.price ?? 0) * (p?.quantity ?? 1), 0) ?? 0,
        platform_code: item?.platform_code,
        custom_id: item?.custom_id,
        status: {
          slug: (item?.status?.slug as OrderStatusEnum) || '',
          merchantTitle: (item?.status?.merchant_title as MerchantTitleOrderStatusEnum) ?? '',
        },
        // Search hits historically lacked this backend-appended flag (the search
        // endpoint returns raw Meilisearch docs); the backend now hydrates it,
        // and dropping it here made searched bulk-collected orders render as
        // plain "Collected" while the default list showed "Bulk Collected".
        is_bulk_collected: item?.is_bulk_collected ?? false,
        // TEMP-9: mirror the default-list mapper — the search path (raw
        // Meilisearch docs) must carry the cold-chain flag too, or searched
        // orders would silently drop the Snowflake pill.
        temperature_control: item?.temperature_control ?? false,
        // R14 — the box count, present on multi-box rows only. Mapped beside
        // temperature_control and for the same reason its comment gives: the
        // search path is a second mapper, and a field carried by only one of
        // them drops its pill on searched rows without anything failing.
        box_count: item?.box_count,
        // BOXY-378 — both mappers, same reason as box_count above.
        partiallyDeliveredAt: item?.partially_delivered_at ?? null,
        pickupLocation: getOrderPickupAddress(item)?.title || '---',
        pickupAddressId: getOrderPickupAddress(item)?.uid || '',
        products: item?.products?.map((product) => ({
          name: product?.title,
          quantity: product?.quantity,
          price: product?.price,
        })),
        driver: {
          phone: item?.driver?.phone,
          assign: Boolean(item?.driver) ?? false,
        },
        carrier_message: item?.carrier_message || '---',
        merchant_action_required: item?.merchant_action_required || 0,
        merchant_action_required_text: orderReasonText(item),
        carrier: item?.carrier,
      })) || []
    );
  };

  const listData = mapOrderData(dataOrderList);

  return {
    listData,
    totalsOrderList: dataOrderList?.object?.total || 0,
    loadingOrderList,
    fetchingOrderList,
  };
};

export default useOrderListData;
